import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Pagination,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { Redirect } from "react-router-dom";
import { FaAddressBook, FaSearch, FaSyncAlt } from "react-icons/fa";
import SidebarPanelNavigation from "../../../../components/SidebarPanelNavigation";
import useAuth from "../../../../contexts/auth";
import {
  listWhatsAppWebChannels,
  getWhatsAppWebChannelContacts,
} from "../../../../services/api";

const PAGE_SIZE = 30;

export default function ContactsPage() {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [channelId, setChannelId] = useState("");
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    listWhatsAppWebChannels().then((res) =>
      setChannels(res?.data?.channels || [])
    );
  }, []);

  const reload = useCallback(async () => {
    if (!channelId) {
      setContacts([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await getWhatsAppWebChannelContacts(channelId);
      // Backend pode retornar { syncing: true } quando o WA Multi-Device
      // ainda está populando os chats (~20s após conectar). Aí auto-retry.
      if (res?.syncing) {
        setSyncing(true);
        setContacts([]);
        setTimeout(() => reload(), 5000);
        return;
      }
      setSyncing(false);
      setContacts(res?.data || []);
      setPage(1);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        "Falha ao buscar contatos. O canal está ativo?";
      setError(msg);
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, [channelId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const filtered = useMemo(() => {
    if (!search) return contacts;
    const term = search.toLowerCase();
    return contacts.filter(
      (c) =>
        (c.name || "").toLowerCase().includes(term) ||
        (c.vname || "").toLowerCase().includes(term) ||
        (c.phone || "").includes(term)
    );
  }, [contacts, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  if (!user) return <Redirect to="/entrar" />;
  if (user.type !== "admin") return <Redirect to="/" />;

  function handleSearch(e) {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  }

  return (
    <Container fluid className="p-4">
      <Row>
        <Col md={3} lg={2}>
          <SidebarPanelNavigation isAdmin />
        </Col>
        <Col md={9} lg={10}>
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div>
              <h2 className="mb-1">
                <FaAddressBook className="mr-2" />
                Contatos
              </h2>
              <p className="text-muted mb-0">
                Agenda do número WhatsApp conectado
              </p>
            </div>
          </div>

          <Card className="mb-3">
            <Card.Body>
              <Row>
                <Col md={4}>
                  <Form.Label>Canal</Form.Label>
                  <Form.Control
                    as="select"
                    value={channelId}
                    onChange={(e) => setChannelId(e.target.value)}
                  >
                    <option value="">Selecione um canal</option>
                    {channels.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} {c.status === "active" ? "✅" : "⚪"}
                      </option>
                    ))}
                  </Form.Control>
                </Col>
                <Col md={6}>
                  <Form onSubmit={handleSearch}>
                    <Form.Label>Buscar</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="text"
                        placeholder="Nome ou telefone..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                      />
                      <InputGroup.Append>
                        <Button type="submit" variant="primary">
                          <FaSearch />
                        </Button>
                      </InputGroup.Append>
                    </InputGroup>
                  </Form>
                </Col>
                <Col md={2} className="d-flex align-items-end">
                  <Button
                    variant="success"
                    onClick={reload}
                    disabled={!channelId || loading}
                    className="w-100"
                  >
                    <FaSyncAlt /> Recarregar
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {error && (
            <Card className="mb-3 border-danger">
              <Card.Body className="text-danger">
                <strong>Erro:</strong> {error}
              </Card.Body>
            </Card>
          )}

          {syncing && (
            <Card className="mb-3 border-warning">
              <Card.Body className="text-warning d-flex align-items-center">
                <Spinner animation="border" size="sm" className="mr-2" />
                <span>
                  <strong>Sincronizando WhatsApp Web...</strong>{" "}
                  O Multi-Device está baixando os contatos. Vai retentar
                  automaticamente em 5s.
                </span>
              </Card.Body>
            </Card>
          )}

          <p className="text-muted">
            Exibindo {pageItems.length} de {filtered.length} contatos
            {totalPages > 1 && ` (página ${page} de ${totalPages})`}
          </p>

          {loading ? (
            <div className="text-center p-5">
              <Spinner animation="border" />
            </div>
          ) : !channelId ? (
            <Card className="text-center p-5">
              <Card.Body>
                <p className="mb-2">Selecione um canal para ver os contatos.</p>
              </Card.Body>
            </Card>
          ) : filtered.length === 0 ? (
            <Card className="text-center p-5">
              <Card.Body>
                <p className="mb-2">Nenhum contato encontrado.</p>
                <small className="text-muted">
                  Confirme que o canal está conectado (status ativo).
                </small>
              </Card.Body>
            </Card>
          ) : (
            <Card>
              <Table striped hover responsive className="mb-0">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Telefone</th>
                    <th>WhatsApp ID</th>
                    <th className="text-center">Verificado</th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map((c) => (
                    <tr key={c.lid || c.phone}>
                      <td>
                        <strong>{c.name || c.vname || "(sem nome)"}</strong>
                        {c.short && c.short !== c.name && (
                          <small className="text-muted d-block">
                            {c.short}
                          </small>
                        )}
                      </td>
                      <td>{c.phone || "-"}</td>
                      <td>
                        <code style={{ fontSize: 11 }}>{c.lid}</code>
                      </td>
                      <td className="text-center">
                        {c.verify === "1" ? (
                          <Badge variant="success">✓</Badge>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          )}

          {totalPages > 1 && (
            <Pagination className="justify-content-center mt-3">
              <Pagination.Prev
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              />
              <Pagination.Item active>
                {page} / {totalPages}
              </Pagination.Item>
              <Pagination.Next
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              />
            </Pagination>
          )}
        </Col>
      </Row>
    </Container>
  );
}
